import React from "react";
import axios from "axios";
import validator from 'validator';
import { notifications } from '@mantine/notifications';
import { Text, Box, Group, Drawer, Button, Textarea, Divider, ActionIcon, TextInput, Badge, Paper } from '@mantine/core';
import { IconX, IconExclamationCircle, IconArrowLeft } from '@tabler/icons-react';

import { useLoginContext } from "../../context/loginContext";
import { BE_API_TWO, utils } from "../../utils/globalVariables";
import { stringDivider, regexNumberOnly, safeValue, getObjectParam2, masaGaransiUtils__1, masaGaransiUtils__2 } from "../../utils/globalFunctions";

import LabelContentInline from "../../components/general/LabelContentInline";
import ModalResponse from "../../components/general/ModalResponse";
import PaperWrapper from "../../components/utilities/PaperWrapper";
import CustomerSelectorDrawer from "../../components/utilities/CustomerSelectorDrawer";
import NotaByCustomerSelectorDrawer from "../../components/utilities/NotaByCustomerSelectorDrawer";

const stepsInclude = [utils.projectProcess.nota.value, utils.projectProcess.antrian.value, utils.projectProcess.pengerjaan.value, utils.projectProcess.qc.value, utils.projectProcess.selesai.value];

const StockItemKlaimGaransi = ({
  stateShow,
  fnShow,
  fnSuccessKlaim,
  stateData,
  stateTrigger
}) => {

  const mountedRef = React.useRef(true);
  const { loginContext, handleSetLoginContext } = useLoginContext();

  const notificationId = React.useRef({
    errorSubmitData: 'StockItemKlaimGaransi-1',
    successSubmitData: 'StockItemKlaimGaransi-2',
  })

  const [data, setData] = React.useState({
    taggedCustomerId: '',
    taggedCustomerName: '',
    taggedProjectId: '',

    jml: '',
    jmlError: '',

    keterangan: '',
    keteranganError: '',

    submitLoading: false,

    customerSelector: {
      show: false,
      dataSelected: {
        triggerUseEffect: new Date(),
      }
    },

    notaSelector: {
      show: false,
      triggerUpdateSelector: new Date(),
      dataSelected: {
        triggerUseEffect: new Date(),
      }
    },

    modalResponse: {
      show: false,
      type: utils.modalResponse.warning,
      hideOnClickOutSide: false,
      content: ''
    }
  });

  React.useEffect(() => {
    return () => {
      mountedRef.current = false;
    }
  }, [])

  React.useEffect(() => {
    setData(d => ({
      ...d,
      taggedCustomerId: stateData.tagged_client_id,
      taggedCustomerName: stateData.customer_name,
      taggedProjectId: stateData.tagged_project_id,
    }));
  }, [stateData, stateTrigger])

  const handleSubmit = () => {
    setData(d => ({ ...d, jmlError: '', keteranganError: '' }));

    if (validator.isEmpty(data.jml, { ignore_whitespace: true })) {
      setData(d => ({ ...d, jmlError: 'Jumlah wajib diisi!' }));
    } else if (Number(data.jml) <= 0) {
      setData(d => ({ ...d, jmlError: 'Input jumlah minimal 1' }));
    } else if (validator.isEmpty(data.keterangan, { ignore_whitespace: true })) {
      setData(d => ({ ...d, keteranganError: 'Keterangan wajib diisi!' }));
    } else {
      setData(d => ({ ...d, submitLoading: true }))

      const formDT = new FormData();
      const formDTHeader = { 'Content-Type': 'application/json' }

      formDT.append('json_token', loginContext.syswebapp);

      formDT.append('stock_id', stateData.stock_id);
      formDT.append('jumlah', data.jml);
      formDT.append('keterangan', data.keterangan);

      formDT.append('tagged_customer_id', data.taggedCustomerId);
      formDT.append('tagged_project_id', data.taggedProjectId);

      formDT.append('claimed_flag', utils.stockItemStatus.claimed.value);
      formDT.append('tagged_flag', utils.stockItemStatus.tagged.value);
      formDT.append('project_steps_include', JSON.stringify(stepsInclude));

      //

      // formDT.append('pembatalan_type', utils.batalPemakaianItem.rusak_ada_garansi.slug);
      // formDT.append('pembatalan_type_list', JSON.stringify(utils.batalPemakaianItem));

      formDT.append('status_item_garansi_pengajuan', utils.statusItemGaransi.pengajuan_klaim_garansi.value);
      formDT.append('garansi_reference', utils.garansiReference.master_stock.value);
      formDT.append('jenis_masa_garansi', JSON.stringify(utils.masaGaransi));

      axios({
        method: 'post',
        url: BE_API_TWO + '/klaim_garansi_from_ms_stock',
        data: formDT,
        headers: formDTHeader,
      }).then((response) => {
        if (!mountedRef.current) return null;

        setData(d => ({ ...d, submitLoading: false }));
        fnShow();

        if (response.data.flag_status === 'isset') {
          setData(d => ({ ...d, jml: '', keterangan: '' }));
          fnSuccessKlaim();
        }

        if (response.data.flag_status === 'no-data') {
          setData(d => ({
            ...d,
            modalResponse: {
              ...d.modalResponse,
              type: utils.modalResponse.warning,
              hideOnClickOutSide: true,
              content: 'Data tidak ditemukan.',
              show: true
            }
          }))
        }

        if (response.data.flag_status === 'input-lebih-besar-dari-aktual') {
          setData(d => ({
            ...d,
            modalResponse: {
              ...d.modalResponse,
              type: utils.modalResponse.warning,
              hideOnClickOutSide: true,
              content: 'Jumlah yang diinput melebihi stok yang ada.',
              show: true
            }
          }))
        }

        if (response.data.flag_status === 'item-cannot-be-claimed') {
          setData(d => ({
            ...d,
            modalResponse: {
              ...d.modalResponse,
              type: utils.modalResponse.warning,
              hideOnClickOutSide: true,
              content: 'Item ini tidak dapat diklaim garansi.',
              show: true
            }
          }))
        }

        if (response.data.flag_status === 'error') {
          notifications.show({
            id: notificationId.current.errorSubmitData,
            title: utils.notification.warning.caption,
            message: response.data.pesan,
            color: utils.notification.warning.color,
            icon: utils.notification.warning.icon,
            withBorder: true,
            autoClose: false,
          })
        }

        if (response.data.flag_status === 'warning') {
          setData(d => ({
            ...d,
            modalResponse: {
              ...d.modalResponse,
              type: utils.modalResponse.warning,
              hideOnClickOutSide: true,
              content: response.data.message,
              show: true
            }
          }))
        }

        if (response.data.flag_status === 'invalid-json-token') {
          handleSetLoginContext(null);
        }

        if (response.data.flag_status === 'user-inactive') {
          handleSetLoginContext(null);
        }
      }).catch(error => {
        if (!mountedRef.current) return null;

        setData(d => ({ ...d, submitLoading: false }))

        fnShow();

        notifications.show({
          id: notificationId.current.errorSubmitData,
          title: utils.notification.warning.caption,
          message: error.message,
          color: utils.notification.warning.color,
          icon: utils.notification.warning.icon,
          withBorder: true,
          autoClose: true,
        })
      })
    }
  }

  return (
    <React.Fragment>
      <Drawer
        opened={stateShow}
        onClose={fnShow}
        withCloseButton={false}
        position="left"
        closeOnClickOutside={true}

        title={
          <Group noWrap spacing='xs'>
            <ActionIcon
              size='lg'
              variant="light"
              radius='xl'
              color="blue"
              onClick={fnShow}
            >
              <IconArrowLeft size='1.25rem' stroke={1.5} />
            </ActionIcon>

            <Text>Klaim Garansi Item</Text>
          </Group>
        }
        size='md'

        styles={() => ({
          title: {
            fontWeight: 'bold',
            textTransform: 'uppercase'
          }
        })}
      >
        <Box py='md' sx={{ minHeight: '500px' }}>

          <Text size='sm' mb='1rem'>
            <LabelContentInline
              stateColLabel={3}
              stateLabel='Item'
              stateContent={
                <Group spacing='xs'>
                  <Text>{`${safeValue(stateData.stock_category_name, ' ')} ${safeValue(stateData.stock_category_sub_name, ' ')} ${safeValue(stateData.stock_serial_number, ' ')}`}</Text>
                  <Badge
                    size='md'
                    color={getObjectParam2(utils.stockItemStatus, stateData.stock_status, 'color', 'gray')}

                    styles={() => ({
                      inner: {
                        fontWeight: 'normal',
                      }
                    })}
                  >
                    {getObjectParam2(utils.stockItemStatus, stateData.stock_status, 'caption', ' - ')}
                  </Badge>
                </Group>
              }
              stateDivider={false}
            />

            <LabelContentInline
              stateColLabel={3}
              stateLabel='Garansi Supplier'
              stateContent={masaGaransiUtils__1({
                stateGaransiType: stateData.stock_masa_garansi_type,
                stateGaransiBulan: stateData.stock_masa_garansi_bulan,
                stateGaransiHari: stateData.stock_masa_garansi_hari,
                stateTanggalPembelian: stateData.stock_tanggal_beli,
                stateShowTanggalPembelian: true,
              })}
              stateDivider={false}
            />

            <LabelContentInline
              stateColLabel={0}
              stateLabel=''
              stateContent={
                <Text
                  color={masaGaransiUtils__2({
                    stateGaransiType: stateData.stock_masa_garansi_type,
                    stateCanBeClaimed: stateData.can_be_claimed,
                    stateColorCanBeClaimed: 'blue',
                    stateColorCannotBeClaimed: 'red'
                  }).colorCanBeClaimed}
                >
                  {masaGaransiUtils__2({
                    stateGaransiType: stateData.stock_masa_garansi_type,
                    stateCanBeClaimed: stateData.can_be_claimed,
                  }).isCanBeClaimedCaption}
                </Text>
              }
              stateDivider={false}
            />
          </Text>

          <Paper
            mb='1rem'
            p='md'

            sx={(tema) => ({
              backgroundColor: tema.colorScheme === 'light' ? tema.colors.gray[0] : tema.colors.dark[6],
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: tema.colorScheme === 'light' ? tema.colors.gray[3] : tema.colors.dark[4],
              borderRadius: tema.radius.sm,
            })}
          >
            <Text
              size='sm'
              tt='uppercase'
            >
              Input jumlah item yang akan diklaim
            </Text>

            <Divider my='md' />

            <TextInput
              mb='sm'
              // variant='filled'
              placeholder="Jumlah"
              label="Jumlah"
              value={data.jml}
              onChange={(val) => setData(d => ({ ...d, jml: regexNumberOnly(val.target.value) }))}
              error={data.jmlError}
            />

            <Textarea
              // variant='filled'
              placeholder="Ketereangan"
              label="Keterangan"
              value={data.keterangan}
              onChange={(val) => setData(d => ({ ...d, keterangan: val.target.value }))}
              error={data.keteranganError}
            />
          </Paper>

          <PaperWrapper
            stateMB="1rem"
          >
            <Group noWrap position="apart">
              <Text size='sm' tt='uppercase'>Tag Customer</Text>

              <Button
                size="xs"
                variant='light'
                onClick={() => {
                  setData(d => ({
                    ...d,
                    customerSelector: {
                      ...d.customerSelector,
                      show: true
                    }
                  }))
                }}
              >
                Pilih
              </Button>
            </Group>

            <Divider mt='sm' mb='md' />

            {stateData.customer_name_tagged_permanent &&
              <Paper
                p='xs'
                mb='sm'
                shadow="xs"
              >
                <Group
                  noWrap
                  spacing='xs'
                >
                  <IconExclamationCircle size="1rem" />
                  <Text size='sm'>Item ini pertama kali di tag untuk <strong>{stateData.customer_name_tagged_permanent}</strong></Text>
                </Group>
              </Paper>
            }

            {data.taggedCustomerId ?
              <Box>
                <Group noWrap position="apart">
                  <Text size='sm'>Tagged untuk {data.taggedCustomerName}</Text>
                  <ActionIcon
                    variant="light"
                    color="blue"
                    radius="xl"
                    onClick={() => {
                      setData(d => ({
                        ...d,
                        taggedCustomerId: '',
                        taggedCustomerName: '',
                        taggedProjectId: ''
                      }))
                    }}
                  >
                    <IconX size="1rem" />
                  </ActionIcon>
                </Group>

                <Text size='sm' color='dimmed'>Item ini akan dikhusukan untuk {data.taggedCustomerName}</Text>
              </Box>
              :
              <Text size='sm' color="dimmed">Pilih customer untuk tagging</Text>
            }
          </PaperWrapper>

          <PaperWrapper
            stateMB="1rem"
          >
            <Group noWrap position="apart">
              <Text size='sm' tt='uppercase'>Tag Nota</Text>

              <Button
                size="xs"
                variant='light'
                onClick={data.taggedCustomerId ?
                  () => setData(d => ({
                    ...d,
                    notaSelector: {
                      ...d.notaSelector,
                      show: true,
                      triggerUpdateSelector: new Date(),
                    }
                  }))
                  :
                  () => setData(d => ({
                    ...d,
                    modalResponse: {
                      ...d.modalResponse,
                      type: utils.modalResponse.warning,
                      hideOnClickOutSide: true,
                      content: 'Tag customer terlebih dahulu.',
                      show: true
                    }
                  }))
                }
              >
                Pilih
              </Button>
            </Group>

            <Divider mt='sm' mb='md' />

            {(data.taggedCustomerId && data.taggedProjectId) ?
              <Box>
                <Group noWrap position="apart">
                  <Text size='sm'>Tagged untuk project {stringDivider(data.taggedProjectId, [4, 6, 8], '.', '[Nomor Nota]')}</Text>
                  <ActionIcon
                    variant="light"
                    color="blue"
                    radius="xl"
                    onClick={() => {
                      setData(d => ({
                        ...d,
                        taggedProjectId: ''
                      }))
                    }}
                  >
                    <IconX size="1rem" />
                  </ActionIcon>
                </Group>
                <Text size='sm' color='dimmed'>Item ini akan dikhusukan untuk {data.taggedCustomerName} dan project (nota) {stringDivider(data.taggedProjectId, [4, 6, 8], '.', '[Nomor Nota]')}</Text>
              </Box>
              :
              <Text size='sm' color="dimmed">Pilih nota untuk tagging</Text>
            }
          </PaperWrapper>

          <Group position="apart" noWrap spacing='xs' my='xl'>
            <Button
              fullWidth
              loading={data.submitLoading}
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </Group>

        </Box>
      </Drawer>

      <ModalResponse
        stateShow={data.modalResponse.show}
        fnHide={() => setData(d => ({
          ...d,
          modalResponse: {
            ...d.modalResponse,
            show: false
          }
        }))}
        stateOverlayZIndex={201}

        stateHideOnClickOutSide={data.modalResponse.hideOnClickOutSide}
        stateType={data.modalResponse.type}
        stateContent={data.modalResponse.content}
      />

      <CustomerSelectorDrawer
        stateShow={data.customerSelector.show}
        fnShow={() => setData(d => ({
          ...d,
          customerSelector: {
            ...d.customerSelector,
            show: false
          }
        }))}

        stateData={{
          customerIsActive: '1',
          customerCategoryIsActive: '1',
        }}

        fnOnSelect={(object) => {
          setData(d => ({
            ...d,
            taggedCustomerId: object.customerId,
            taggedCustomerName: object.customerName,
            taggedProjectId: '',

            customerSelector: {
              ...d.customerSelector,
              show: false,
              dataSelected: {
                ...d.customerSelector.dataSelected,
                triggerUseEffect: new Date()
              }
            }
          }))
        }}
      />

      <NotaByCustomerSelectorDrawer
        stateShow={data.notaSelector.show}
        fnShow={() => setData(d => ({
          ...d,
          notaSelector: {
            ...d.notaSelector,
            show: false
          }
        }))}

        stateData={{
          customerId: data.taggedCustomerId,
          projectIsCancel: '0',
          triggerUpdateSelector: data.notaSelector.triggerUpdateSelector,
        }}

        fnOnSelect={(object) => {
          setData(d => ({
            ...d,
            taggedProjectId: object.projectId,

            notaSelector: {
              ...d.notaSelector,
              show: false,
              dataSelected: {
                ...d.notaSelector.dataSelected,
                triggerUseEffect: new Date()
              }
            }
          }))
        }}
      />
    </React.Fragment>
  )
}

export default StockItemKlaimGaransi;